/**
 * Skill Editor - HTML Overlay UI
 * Renders as DOM elements over the game canvas.
 * Much better UX than Canvas-based UI.
 */

import { Player } from '@client/entities/Player';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import {
  SkillDeliveryType,
  DELIVERY_TYPE_INFO,
  DELIVERY_TYPE_SLIDERS,
  DELIVERY_TYPES,
  ELEMENTS,
  ELEMENT_INFO,
  SKILL_CHOICES,
} from '@shared/skills/SkillDeliveryTypes';
import { ElementType } from '@shared/types';

// ========================
// CSS Styles (injected once)
// ========================

const CSS = `
.skill-editor-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
}

.skill-editor-panel {
  background: linear-gradient(180deg, #1a1510, #0d0a08);
  border: 2px solid #3a3530;
  border-radius: 10px;
  width: 85vw;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.se-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #3a3530;
  flex-shrink: 0;
}

.se-header h2 {
  color: #ffd700;
  font-size: 20px;
  margin: 0;
}

.se-close {
  background: #3d3228;
  border: 1px solid #5a4a3a;
  color: #f4e8d4;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.se-close:hover { background: #5a4a3a; }

.se-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Left: Skill List */
.se-list {
  width: 220px;
  min-width: 180px;
  border-right: 1px solid #3a3530;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.se-list-header {
  padding: 8px;
  border-bottom: 1px solid #2a2520;
}

.se-new-btn {
  width: 100%;
  padding: 8px;
  background: #2a4a2a;
  border: 1px solid #3a6a3a;
  color: #88dd88;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.se-new-btn:hover { background: #3a6a3a; }

.se-list-items {
  flex: 1;
  overflow-y: auto;
  padding: 4px;
}

.se-skill-item {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  transition: background 0.15s;
}
.se-skill-item:hover { background: #2a2520; }
.se-skill-item.active { background: #2a2520; border: 1px solid #ffd700; }

.se-skill-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.se-skill-name { flex: 1; }
.se-skill-type { color: #8a7e6e; font-size: 10px; }

/* Right: Editor */
.se-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.se-editor-header {
  padding: 12px 16px;
  border-bottom: 1px solid #2a2520;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.se-skill-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.se-element-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
}

.se-type-label {
  color: #8a7e6e;
  font-size: 12px;
}

.se-cost-info {
  text-align: right;
  font-size: 11px;
  color: #6a9cda;
}

.se-tabs {
  display: flex;
  border-bottom: 1px solid #2a2520;
  flex-shrink: 0;
}

.se-tab {
  padding: 8px 20px;
  cursor: pointer;
  color: #8a7e6e;
  font-size: 12px;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
}
.se-tab:hover { color: #f4e8d4; }
.se-tab.active { color: #ffd700; border-bottom-color: #ffd700; }

.se-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

/* Choices */
.se-choice-group {
  margin-bottom: 12px;
}

.se-choice-label {
  color: #ffd700;
  font-size: 11px;
  font-weight: bold;
  margin-bottom: 6px;
}

.se-choice-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.se-choice-btn {
  flex: 1;
  min-width: 60px;
  padding: 6px 8px;
  background: #151310;
  border: 1px solid #3a3530;
  color: #8a7e6e;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  text-align: center;
  transition: all 0.15s;
}
.se-choice-btn:hover { background: #2a2520; color: #f4e8d4; }
.se-choice-btn.active {
  background: #2a2520;
  border-color: #ffd700;
  color: #f4e8d4;
}

/* Sliders */
.se-slider-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.se-slider-label {
  width: 100px;
  font-size: 11px;
  color: #8a7e6e;
  flex-shrink: 0;
}

.se-slider-track {
  flex: 1;
  height: 8px;
  background: #2a2520;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
}

.se-slider-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.1s;
}

.se-slider-input {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
  margin: 0;
}

.se-slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: #f4e8d4;
  border-radius: 50%;
  border: 2px solid #3a3530;
  cursor: grab;
  margin-top: -4px;
}

.se-slider-value {
  width: 80px;
  text-align: right;
  font-size: 11px;
  color: #f4e8d4;
  font-family: monospace;
  flex-shrink: 0;
}

/* Bottom: Hotbar + Delete */
.se-bottom {
  border-top: 1px solid #3a3530;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.se-hotbar {
  display: flex;
  gap: 4px;
  flex: 1;
}

.se-hotbar-slot {
  width: 36px;
  height: 36px;
  background: #151310;
  border: 1px solid #3a3530;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #8a7e6e;
  cursor: pointer;
}
.se-hotbar-slot:hover { border-color: #ffd700; }
.se-hotbar-slot.assigned { border-color: #5a5; background: #1a2a1a; }

.se-delete-btn {
  padding: 8px 16px;
  background: #4a1a1a;
  border: 1px solid #6a2a2a;
  color: #ff6666;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.se-delete-btn:hover { background: #6a2a2a; }

/* Creation flow */
.se-create-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 16px;
}

.se-create-type {
  padding: 12px;
  background: #151310;
  border: 1px solid #3a3530;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.se-create-type:hover { background: #2a2520; border-color: #5a4a3a; }

.se-create-type-icon { font-size: 20px; }
.se-create-type-name { font-size: 13px; font-weight: bold; margin-top: 4px; }
.se-create-type-desc { font-size: 10px; color: #8a7e6e; margin-top: 2px; }

.se-element-grid {
  display: flex;
  gap: 8px;
  padding: 16px;
  justify-content: center;
}

.se-element-btn {
  padding: 12px 20px;
  background: #151310;
  border: 2px solid #3a3530;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  transition: all 0.15s;
}
.se-element-btn:hover { background: #2a2520; }

.se-back-btn {
  padding: 8px 16px;
  background: #2a2520;
  border: 1px solid #3a3530;
  color: #8a7e6e;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  margin: 8px 16px;
}
.se-back-btn:hover { background: #3a3530; color: #f4e8d4; }

.se-empty-msg {
  color: #5a4a3a;
  text-align: center;
  padding: 40px;
  font-size: 14px;
}

/* Stats tab */
.se-stat-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
  border-bottom: 1px solid #1a1815;
}
.se-stat-label { color: #8a7e6e; }
.se-stat-value { color: #f4e8d4; font-family: monospace; }

/* Scrollbar */
.se-list-items::-webkit-scrollbar,
.se-content::-webkit-scrollbar {
  width: 6px;
}
.se-list-items::-webkit-scrollbar-track,
.se-content::-webkit-scrollbar-track {
  background: #0d0a08;
}
.se-list-items::-webkit-scrollbar-thumb,
.se-content::-webkit-scrollbar-thumb {
  background: #3a3530;
  border-radius: 3px;
}
`;

// ========================
// Skill Editor HTML UI
// ========================

type CreateStep = 'none' | 'type' | 'element';

export class SkillEditorHTML {
  private player: Player;
  private container: HTMLDivElement | null = null;
  private _isOpen = false;
  private selectedIndex = 0;
  private activeTab: 'settings' | 'stats' = 'settings';
  private createStep: CreateStep = 'none';
  private pendingDeliveryType: SkillDeliveryType | null = null;
  private styleInjected = false;
  private callbacks: { onSkillChange?: () => void } = {};

  constructor(player: Player) {
    this.player = player;
  }

  setCallbacks(cb: { onSkillChange?: () => void }): void {
    this.callbacks = cb;
  }

  open(): void {
    this._isOpen = true;
    this.createStep = 'none';
    this.render();
  }

  close(): void {
    this._isOpen = false;
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  toggle(): void { this._isOpen ? this.close() : this.open(); }
  isVisible(): boolean { return this._isOpen; }

  private injectStyles(): void {
    if (this.styleInjected) return;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    this.styleInjected = true;
  }

  private render(): void {
    if (!this._isOpen) return;
    this.injectStyles();

    if (this.container) this.container.remove();

    this.container = document.createElement('div');
    this.container.className = 'skill-editor-overlay';
    this.container.addEventListener('mousedown', (e) => {
      if (e.target === this.container) this.close();
    });

    const panel = document.createElement('div');
    panel.className = 'skill-editor-panel';

    // Header
    panel.appendChild(this.buildHeader());

    // Body
    const body = document.createElement('div');
    body.className = 'se-body';

    if (this.createStep !== 'none') {
      body.appendChild(this.buildCreationFlow());
    } else {
      body.appendChild(this.buildSkillList());
      body.appendChild(this.buildEditor());
    }

    panel.appendChild(body);

    // Bottom bar
    if (this.createStep === 'none') {
      panel.appendChild(this.buildBottomBar());
    }

    this.container.appendChild(panel);
    document.body.appendChild(this.container);
  }

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'se-header';

    const title = document.createElement('h2');
    title.textContent = 'Beceri Editörü';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'se-close';
    closeBtn.textContent = '✕';
    closeBtn.onclick = () => this.close();
    header.appendChild(closeBtn);

    return header;
  }

  private buildSkillList(): HTMLElement {
    const list = document.createElement('div');
    list.className = 'se-list';

    // New button
    const listHeader = document.createElement('div');
    listHeader.className = 'se-list-header';
    const newBtn = document.createElement('button');
    newBtn.className = 'se-new-btn';
    newBtn.textContent = '+ Yeni Beceri';
    newBtn.onclick = () => { this.createStep = 'type'; this.render(); };
    listHeader.appendChild(newBtn);
    list.appendChild(listHeader);

    // Skill items
    const items = document.createElement('div');
    items.className = 'se-list-items';

    const skills = this.player.customSkillsV2;
    if (skills.length === 0) {
      const msg = document.createElement('div');
      msg.className = 'se-empty-msg';
      msg.textContent = 'Henüz beceri yok';
      items.appendChild(msg);
    }

    skills.forEach((skill, i) => {
      const item = document.createElement('div');
      item.className = `se-skill-item${i === this.selectedIndex ? ' active' : ''}`;
      item.onclick = () => { this.selectedIndex = i; this.render(); };

      const dot = document.createElement('div');
      dot.className = 'se-skill-dot';
      dot.style.background = ELEMENT_INFO[skill.element]?.color || '#888';
      item.appendChild(dot);

      const nameDiv = document.createElement('div');
      nameDiv.className = 'se-skill-name';
      nameDiv.textContent = skill.name;
      item.appendChild(nameDiv);

      const typeDiv = document.createElement('div');
      typeDiv.className = 'se-skill-type';
      typeDiv.textContent = DELIVERY_TYPE_INFO[skill.deliveryType]?.icon || '';
      item.appendChild(typeDiv);

      items.appendChild(item);
    });

    list.appendChild(items);
    return list;
  }

  private buildEditor(): HTMLElement {
    const editor = document.createElement('div');
    editor.className = 'se-editor';

    const skills = this.player.customSkillsV2;
    if (skills.length === 0 || this.selectedIndex >= skills.length) {
      const msg = document.createElement('div');
      msg.className = 'se-empty-msg';
      msg.textContent = 'Beceri seç veya yeni oluştur';
      editor.appendChild(msg);
      return editor;
    }

    const skill = skills[this.selectedIndex];

    // Editor header
    editor.appendChild(this.buildEditorHeader(skill));

    // Tabs
    editor.appendChild(this.buildTabs());

    // Content
    const content = document.createElement('div');
    content.className = 'se-content';

    if (this.activeTab === 'settings') {
      this.buildChoices(content, skill);
      this.buildSliders(content, skill);
    } else {
      this.buildStats(content, skill);
    }

    editor.appendChild(content);
    return editor;
  }

  private buildEditorHeader(skill: CustomSkillV2): HTMLElement {
    const header = document.createElement('div');
    header.className = 'se-editor-header';

    const info = document.createElement('div');
    info.className = 'se-skill-info';

    const name = document.createElement('span');
    name.textContent = skill.name;
    name.style.fontWeight = 'bold';
    info.appendChild(name);

    const elemBadge = document.createElement('span');
    elemBadge.className = 'se-element-badge';
    const elemInfo = ELEMENT_INFO[skill.element];
    elemBadge.textContent = `${elemInfo?.icon || ''} ${elemInfo?.name || skill.element}`;
    elemBadge.style.background = (elemInfo?.color || '#888') + '33';
    elemBadge.style.color = elemInfo?.color || '#888';
    info.appendChild(elemBadge);

    const typeLabel = document.createElement('span');
    typeLabel.className = 'se-type-label';
    const typeInfo = DELIVERY_TYPE_INFO[skill.deliveryType];
    typeLabel.textContent = `${typeInfo?.icon || ''} ${typeInfo?.name || skill.deliveryType}`;
    info.appendChild(typeLabel);

    header.appendChild(info);

    const costDiv = document.createElement('div');
    costDiv.className = 'se-cost-info';
    if (skill.manaDrainPerSecond) {
      costDiv.textContent = `${skill.manaDrainPerSecond.toFixed(0)} mana/s`;
    } else if (skill.manaReservePercent) {
      costDiv.textContent = `${skill.manaReservePercent.toFixed(0)}% reserve`;
    } else if (skill.manaReserveFlat) {
      costDiv.textContent = `${skill.manaReserveFlat} mana reserve`;
    } else {
      if (skill.deliveryType === 'melee' || skill.deliveryType === 'projectile') {
        costDiv.textContent = `Mana: ${skill.manaCost.toFixed(0)}  Saldırı Hızı bazlı`;
      } else {
        costDiv.textContent = `Mana: ${skill.manaCost.toFixed(0)}  CD: ${skill.cooldown.toFixed(1)}s`;
      }
    }
    header.appendChild(costDiv);

    return header;
  }

  private buildTabs(): HTMLElement {
    const tabs = document.createElement('div');
    tabs.className = 'se-tabs';

    for (const tab of ['settings', 'stats'] as const) {
      const btn = document.createElement('div');
      btn.className = `se-tab${this.activeTab === tab ? ' active' : ''}`;
      btn.textContent = tab === 'settings' ? 'Ayarlar' : 'İstatistik';
      btn.onclick = () => { this.activeTab = tab; this.render(); };
      tabs.appendChild(btn);
    }

    return tabs;
  }

  private buildChoices(parent: HTMLElement, skill: CustomSkillV2): void {
    const groups = this.getChoiceGroups(skill.deliveryType);

    for (const group of groups) {
      const groupDiv = document.createElement('div');
      groupDiv.className = 'se-choice-group';

      const label = document.createElement('div');
      label.className = 'se-choice-label';
      label.textContent = group.label;
      groupDiv.appendChild(label);

      const buttons = document.createElement('div');
      buttons.className = 'se-choice-buttons';

      const currentVal = (skill as any)[group.key] || Object.keys(group.options)[0];

      for (const [optKey, opt] of Object.entries(group.options)) {
        const btn = document.createElement('button');
        btn.className = `se-choice-btn${currentVal === optKey ? ' active' : ''}`;
        btn.textContent = `${opt.icon} ${opt.name}`;
        btn.title = opt.desc;
        btn.onclick = () => {
          (skill as any)[group.key] = optKey;
          if (group.key === 'aoeShape') {
            (skill.sliders as any).shape = optKey;
          }
          this.callbacks.onSkillChange?.();
          this.render();
        };
        buttons.appendChild(btn);
      }

      groupDiv.appendChild(buttons);
      parent.appendChild(groupDiv);
    }
  }

  private buildSliders(parent: HTMLElement, skill: CustomSkillV2): void {
    const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
    if (!configs) return;

    const elemColor = ELEMENT_INFO[skill.element]?.color || '#888';
    const sliders = skill.sliders as Record<string, number>;

    for (const cfg of configs) {
      const val = sliders[cfg.key];
      if (val === undefined || typeof val !== 'number') continue;

      // Melee: show thrustRange only for thrust, show range only for strike
      if (skill.deliveryType === 'melee') {
        if (cfg.key === 'thrustRange' && skill.meleeType !== 'thrust') continue;
        if (cfg.key === 'range' && skill.meleeType === 'thrust') continue;
      }

      // Projectile: show pierce/chain/forkCount sliders based on contact choice
      if (skill.deliveryType === 'projectile') {
        const contact = skill.projectileContact || 'none';
        if (cfg.key === 'pierce' && contact !== 'pierce') continue;
        if (cfg.key === 'chain' && contact !== 'chain') continue;
        if (cfg.key === 'forkCount' && contact !== 'fork') continue;
      }

      // AOE: show/hide sliders based on shape, trigger, position choices
      if (skill.deliveryType === 'aoe') {
        const shape = (skill.sliders as any).shape || 'circle';
        const trigger = skill.aoeTrigger || 'instant';
        const position = skill.aoePosition || 'target';
        // radius: only circle and leap
        if (cfg.key === 'radius' && shape !== 'circle' && shape !== 'leap') continue;
        // coneAngle: only cone
        if (cfg.key === 'coneAngle' && shape !== 'cone') continue;
        // duration: only persistent trigger
        if (cfg.key === 'duration' && trigger !== 'persistent') continue;
        // delay: only delayed trigger
        if (cfg.key === 'delay' && trigger !== 'delayed') continue;
        // range: only target position (self = no range needed)
        if (cfg.key === 'range' && position === 'self') continue;
      }

      const row = document.createElement('div');
      row.className = 'se-slider-row';

      const label = document.createElement('div');
      label.className = 'se-slider-label';
      label.textContent = cfg.label;
      row.appendChild(label);

      const trackContainer = document.createElement('div');
      trackContainer.className = 'se-slider-track';

      const fill = document.createElement('div');
      fill.className = 'se-slider-fill';
      fill.style.background = elemColor;
      fill.style.width = `${((val - cfg.min) / (cfg.max - cfg.min)) * 100}%`;
      trackContainer.appendChild(fill);

      const input = document.createElement('input');
      input.type = 'range';
      input.className = 'se-slider-input';
      input.min = String(cfg.min);
      input.max = String(cfg.max);
      input.step = String(cfg.step);
      input.value = String(val);
      input.oninput = () => {
        const newVal = Number(input.value);
        SkillCreatorV2.adjustSlider(skill, cfg.key, newVal);
        this.callbacks.onSkillChange?.();
        // Update fill and value display without full re-render
        fill.style.width = `${((newVal - cfg.min) / (cfg.max - cfg.min)) * 100}%`;
        valueDiv.textContent = `${newVal} ${cfg.unit || ''}`;
        // Update cost display
        const costDiv = this.container?.querySelector('.se-cost-info');
        if (costDiv) {
          if (skill.manaDrainPerSecond) costDiv.textContent = `${skill.manaDrainPerSecond.toFixed(0)} mana/s`;
          else if (skill.manaReservePercent) costDiv.textContent = `${skill.manaReservePercent.toFixed(0)}% reserve`;
          else if (skill.deliveryType === 'melee' || skill.deliveryType === 'projectile') costDiv.textContent = `Mana: ${skill.manaCost.toFixed(0)}  Saldırı Hızı bazlı`;
          else costDiv.textContent = `Mana: ${skill.manaCost.toFixed(0)}  CD: ${skill.cooldown.toFixed(1)}s`;
        }
      };
      trackContainer.appendChild(input);
      row.appendChild(trackContainer);

      const valueDiv = document.createElement('div');
      valueDiv.className = 'se-slider-value';
      valueDiv.textContent = `${val} ${cfg.unit || ''}`;
      row.appendChild(valueDiv);

      parent.appendChild(row);
    }
  }

  private buildStats(parent: HTMLElement, skill: CustomSkillV2): void {
    const stats = [
      (skill.deliveryType === 'melee' || skill.deliveryType === 'projectile')
        ? ['Hasar', `Saldırı Hasarının %${(skill.sliders as any).damage}`]
        : ['Hasar', `${SkillCreatorV2.getEffectiveDamage(skill).toFixed(0)}`],
      ['Mana Maliyeti', `${skill.manaCost.toFixed(0)}`],
      (skill.deliveryType === 'melee' || skill.deliveryType === 'projectile')
        ? ['Hız', 'Saldırı Hızına bağlı']
        : ['Bekleme Süresi', `${skill.cooldown.toFixed(1)}s`],
      ['Seviye', `${skill.level}`],
      ['Teslimat', `${DELIVERY_TYPE_INFO[skill.deliveryType]?.name || skill.deliveryType}`],
      ['Element', `${ELEMENT_INFO[skill.element]?.name || skill.element}`],
    ];

    if (skill.deliveryType === 'projectile') {
      stats.push(['Mermi Hızı', `${SkillCreatorV2.getProjectileSpeed(skill).toFixed(0)} px/s`]);
      stats.push(['Mermi Sayısı', `${SkillCreatorV2.getProjectileCount(skill)}`]);
    }

    if (skill.deliveryType === 'aoe') {
      stats.push(['Alan', `${SkillCreatorV2.getEffectiveRadius(skill).toFixed(0)} px`]);
    }

    for (const [label, value] of stats) {
      const row = document.createElement('div');
      row.className = 'se-stat-row';

      const labelDiv = document.createElement('span');
      labelDiv.className = 'se-stat-label';
      labelDiv.textContent = label;
      row.appendChild(labelDiv);

      const valDiv = document.createElement('span');
      valDiv.className = 'se-stat-value';
      valDiv.textContent = value;
      row.appendChild(valDiv);

      parent.appendChild(row);
    }
  }

  private buildBottomBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'se-bottom';

    const hotbar = document.createElement('div');
    hotbar.className = 'se-hotbar';

    for (let i = 0; i < 6; i++) {
      const slot = document.createElement('div');
      slot.className = 'se-hotbar-slot';

      const assignedSkill = this.player.skillHotbar.get(i);
      const currentSkill = this.player.customSkillsV2[this.selectedIndex];

      if (assignedSkill && currentSkill && assignedSkill.id === currentSkill.id) {
        slot.classList.add('assigned');
      }

      slot.textContent = `${i + 1}`;
      slot.onclick = () => {
        const skill = this.player.customSkillsV2[this.selectedIndex];
        if (skill) {
          this.player.skillHotbar.assign(skill, i);
          this.callbacks.onSkillChange?.();
          this.render();
        }
      };
      hotbar.appendChild(slot);
    }

    bar.appendChild(hotbar);

    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'se-delete-btn';
    deleteBtn.textContent = 'Sil';
    deleteBtn.onclick = () => {
      if (this.player.customSkillsV2.length > 0) {
        this.player.customSkillsV2.splice(this.selectedIndex, 1);
        this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        this.callbacks.onSkillChange?.();
        this.render();
      }
    };
    bar.appendChild(deleteBtn);

    return bar;
  }

  private buildCreationFlow(): HTMLElement {
    const container = document.createElement('div');
    container.style.flex = '1';
    container.style.overflow = 'auto';

    if (this.createStep === 'type') {
      const backBtn = document.createElement('button');
      backBtn.className = 'se-back-btn';
      backBtn.textContent = '← Geri';
      backBtn.onclick = () => { this.createStep = 'none'; this.render(); };
      container.appendChild(backBtn);

      const title = document.createElement('div');
      title.style.cssText = 'color: #ffd700; font-size: 16px; padding: 8px 16px; font-weight: bold;';
      title.textContent = 'Teslimat Tipi Seç';
      container.appendChild(title);

      const grid = document.createElement('div');
      grid.className = 'se-create-grid';

      for (const dt of DELIVERY_TYPES) {
        const info = DELIVERY_TYPE_INFO[dt];
        const item = document.createElement('div');
        item.className = 'se-create-type';
        item.style.borderLeft = `3px solid ${info.color}`;

        item.innerHTML = `
          <div class="se-create-type-icon">${info.icon}</div>
          <div class="se-create-type-name">${info.name}</div>
          <div class="se-create-type-desc">${info.description}</div>
        `;

        item.onclick = () => {
          this.pendingDeliveryType = dt;
          this.createStep = 'element';
          this.render();
        };

        grid.appendChild(item);
      }

      container.appendChild(grid);

    } else if (this.createStep === 'element') {
      const backBtn = document.createElement('button');
      backBtn.className = 'se-back-btn';
      backBtn.textContent = '← Geri';
      backBtn.onclick = () => { this.createStep = 'type'; this.render(); };
      container.appendChild(backBtn);

      const title = document.createElement('div');
      title.style.cssText = 'color: #ffd700; font-size: 16px; padding: 8px 16px; font-weight: bold;';
      title.textContent = 'Element Seç';
      container.appendChild(title);

      const grid = document.createElement('div');
      grid.className = 'se-element-grid';

      for (const elem of ELEMENTS) {
        const info = ELEMENT_INFO[elem];
        const btn = document.createElement('div');
        btn.className = 'se-element-btn';
        btn.style.borderColor = info.color;

        btn.innerHTML = `
          <div style="font-size: 24px">${info.icon}</div>
          <div style="margin-top: 4px; font-size: 12px">${info.name}</div>
        `;

        btn.onclick = () => {
          if (this.pendingDeliveryType) {
            const typeName = DELIVERY_TYPE_INFO[this.pendingDeliveryType]?.name || this.pendingDeliveryType;
            const elemName = ELEMENT_INFO[elem]?.name || elem;
            const newSkill = SkillCreatorV2.createSkill(this.pendingDeliveryType, elem as ElementType, `${elemName} ${typeName}`);
            this.player.customSkillsV2.push(newSkill);
            this.selectedIndex = this.player.customSkillsV2.length - 1;
            this.callbacks.onSkillChange?.();
          }
          this.createStep = 'none';
          this.pendingDeliveryType = null;
          this.render();
        };

        grid.appendChild(btn);
      }

      container.appendChild(grid);
    }

    return container;
  }

  private getChoiceGroups(dt: SkillDeliveryType): { key: string; label: string; options: Record<string, { name: string; icon: string; desc: string }> }[] {
    const groups: { key: string; label: string; options: Record<string, { name: string; icon: string; desc: string }> }[] = [];
    switch (dt) {
      case 'melee':
        groups.push({ key: 'meleeType', label: 'Vuruş Tipi', options: SKILL_CHOICES.meleeType });
        break;
      case 'projectile':
        groups.push({ key: 'projectileContact', label: 'Temas Davranışı', options: SKILL_CHOICES.projectileContact });
        break;
      case 'aoe':
        groups.push(
          { key: 'aoeShape', label: 'Şekil', options: { circle: { name: 'Daire', icon: '⭕', desc: 'Dairesel' }, cone: { name: 'Koni', icon: '🔺', desc: 'Yelpaze' }, line: { name: 'Çizgi', icon: '➖', desc: 'Duvar' }, leap: { name: 'Sıçrama', icon: '⬆', desc: 'Atla+patla' } } },
          { key: 'aoeTrigger', label: 'Tetikleme', options: SKILL_CHOICES.aoeTrigger },
          { key: 'aoePosition', label: 'Konum', options: SKILL_CHOICES.aoePosition },
        );
        break;
      case 'beam':
        groups.push({ key: 'beamMode', label: 'Kanal Modu', options: SKILL_CHOICES.beamMode });
        break;
      case 'buff':
        groups.push({ key: 'buffType', label: 'Buff Tipi', options: SKILL_CHOICES.buffType });
        break;
      case 'trap':
        groups.push({ key: 'trapTrigger', label: 'Tetikleme', options: SKILL_CHOICES.trapTrigger });
        break;
      case 'totem':
        groups.push({ key: 'totemType', label: 'Totem Tipi', options: SKILL_CHOICES.totemType });
        break;
      case 'curse':
        groups.push(
          { key: 'curseType', label: 'Lanet Tipi', options: SKILL_CHOICES.curseType },
          { key: 'curseTarget', label: 'Hedef', options: SKILL_CHOICES.curseTarget },
        );
        break;
      case 'minion':
        groups.push({ key: 'minionType', label: 'Minyon Tipi', options: SKILL_CHOICES.minionType });
        break;
    }
    return groups;
  }
}
